import { BigNumber } from '@ethersproject/bignumber';
import type { UseQueryOptions, UseQueryResult } from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';
import { useVenoSDK } from '@ui/providers/VenoSDKProvider';
import { getPendingVno } from '@ui/queries/sdk';
import type { GardenType } from '@ui/utils/garden';
import { currentWallet } from '@veno-app/wallet';

import type { CoinMode, CoinModeCro } from './useCoinMode';
import { useCoinMode } from './useCoinMode';
import { useVnoUsdValue } from './useVnoUsdValue';

const { useAccount } = currentWallet;

const usePendingVno = (
  type: GardenType,
  options?: Partial<Pick<UseQueryOptions, 'enabled'>>,
) => {
  const coinMode = useCoinMode();
  const sdk = useVenoSDK();
  const account = useAccount();

  const query = getPendingVno(coinMode, sdk, account ?? '', type);
  const { data: pendingVno, isLoading: pendingVnoLoading } = useQuery(
    query.queryKey,
    query.queryFn,
    {
      ...options,
      refetchInterval: 8000,
      enabled: !!account && (options?.enabled === undefined || options.enabled),
    },
  );

  return {
    loading: pendingVnoLoading,
    pendingVno,
  };
};

export const usePendingVnoAllGardens = () => {
  const sdk = useVenoSDK();
  const account = useAccount();

  const resultByCoinmode: Record<
    CoinModeCro,
    Record<GardenType, UseQueryResult<BigNumber, unknown>>
  > = {
    cro: {
      FERRO: useQuery(getPendingVno('cro', sdk, account ?? '', 'FERRO')),
      VVS: useQuery(getPendingVno('cro', sdk, account ?? '', 'VVS')),
      TECTONIC: useQuery(getPendingVno('cro', sdk, account ?? '', 'TECTONIC')),
      VVS_CRO: useQuery(getPendingVno('cro', sdk, account ?? '', 'VVS_CRO')),
      VVS_TIA: useQuery({ queryFn: () => BigNumber.from(0) }),
    },
    atom: {
      FERRO: useQuery(getPendingVno('atom', sdk, account ?? '', 'FERRO')),
      VVS: useQuery(getPendingVno('atom', sdk, account ?? '', 'VVS')),
      TECTONIC: useQuery(getPendingVno('atom', sdk, account ?? '', 'TECTONIC')),
      VVS_CRO: useQuery(getPendingVno('atom', sdk, account ?? '', 'VVS_CRO')),
      VVS_TIA: useQuery({ queryFn: () => BigNumber.from(0) }),
    },
    tia: {
      FERRO: useQuery(getPendingVno('tia', sdk, account ?? '', 'FERRO')),
      VVS: useQuery(getPendingVno('tia', sdk, account ?? '', 'VVS')),
      TECTONIC: useQuery(getPendingVno('tia', sdk, account ?? '', 'TECTONIC')),
      VVS_CRO: useQuery(getPendingVno('tia', sdk, account ?? '', 'VVS_CRO')),
      VVS_TIA: useQuery(getPendingVno('tia', sdk, account ?? '', 'VVS_TIA')),
    },
  };

  const vnoUsdValuesMap: Record<
    CoinModeCro,
    Partial<Record<GardenType, BigNumber>>
  > = {
    cro: {
      FERRO: useVnoUsdValue(resultByCoinmode.cro.FERRO.data).data,
      VVS: useVnoUsdValue(resultByCoinmode.cro.VVS.data).data,
      TECTONIC: useVnoUsdValue(resultByCoinmode.cro.TECTONIC.data).data,
      VVS_CRO: useVnoUsdValue(resultByCoinmode.cro.VVS_CRO.data).data,
    },
    atom: {
      FERRO: useVnoUsdValue(resultByCoinmode.atom.FERRO.data).data,
      VVS: useVnoUsdValue(resultByCoinmode.atom.VVS.data).data,
      TECTONIC: useVnoUsdValue(resultByCoinmode.atom.TECTONIC.data).data,
      VVS_CRO: useVnoUsdValue(resultByCoinmode.atom.VVS_CRO.data).data,
    },
    tia: {
      FERRO: useVnoUsdValue(resultByCoinmode.tia.FERRO.data).data,
      VVS: useVnoUsdValue(resultByCoinmode.tia.VVS.data).data,
      TECTONIC: useVnoUsdValue(resultByCoinmode.tia.TECTONIC.data).data,
      VVS_CRO: useVnoUsdValue(resultByCoinmode.tia.VVS_CRO.data).data,
    },
  };

  return {
    data: resultByCoinmode,
    usdData: vnoUsdValuesMap as Record<
      CoinMode,
      Partial<Record<GardenType, BigNumber>>
    >,
  };
};

export default usePendingVno;
